/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name incrapcorr
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/apcorr}
*/
import incrapcorr from './../../incr/apcorr';
setReadOnly( ns, 'incrapcorr', incrapcorr );

/**
* @name incrcount
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/count}
*/
import incrcount from './../../incr/count';
setReadOnly( ns, 'incrcount', incrcount );

/**
* @name incrcovariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/covariance}
*/
import incrcovariance from './../../incr/covariance';
setReadOnly( ns, 'incrcovariance', incrcovariance );

/**
* @name incrcovmat
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/covmat}
*/
import incrcovmat from './../../incr/covmat';
setReadOnly( ns, 'incrcovmat', incrcovmat );

/**
* @name incrcv
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/cv}
*/
import incrcv from './../../incr/cv';
setReadOnly( ns, 'incrcv', incrcv );

/**
* @name increwmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/ewmean}
*/
import increwmean from './../../incr/ewmean';
setReadOnly( ns, 'increwmean', increwmean );

/**
* @name increwstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/ewstdev}
*/
import increwstdev from './../../incr/ewstdev';
setReadOnly( ns, 'increwstdev', increwstdev );

/**
* @name increwvariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/ewvariance}
*/
import increwvariance from './../../incr/ewvariance';
setReadOnly( ns, 'increwvariance', increwvariance );

/**
* @name incrgmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/gmean}
*/
import incrgmean from './../../incr/gmean';
setReadOnly( ns, 'incrgmean', incrgmean );

/**
* @name incrgrubbs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/grubbs}
*/
import incrgrubbs from './../../incr/grubbs';
setReadOnly( ns, 'incrgrubbs', incrgrubbs );

/**
* @name incrhmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/hmean}
*/
import incrhmean from './../../incr/hmean';
setReadOnly( ns, 'incrhmean', incrhmean );

/**
* @name incrkurtosis
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/kurtosis}
*/
import incrkurtosis from './../../incr/kurtosis';
setReadOnly( ns, 'incrkurtosis', incrkurtosis );

/**
* @name incrmaape
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/maape}
*/
import incrmaape from './../../incr/maape';
setReadOnly( ns, 'incrmaape', incrmaape );

/**
* @name incrmae
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mae}
*/
import incrmae from './../../incr/mae';
setReadOnly( ns, 'incrmae', incrmae );

/**
* @name incrmapcorr
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mapcorr}
*/
import incrmapcorr from './../../incr/mapcorr';
setReadOnly( ns, 'incrmapcorr', incrmapcorr );

/**
* @name incrmape
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mape}
*/
import incrmape from './../../incr/mape';
setReadOnly( ns, 'incrmape', incrmape );

/**
* @name incrmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/max}
*/
import incrmax from './../../incr/max';
setReadOnly( ns, 'incrmax', incrmax );

/**
* @name incrmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/maxabs}
*/
import incrmaxabs from './../../incr/maxabs';
setReadOnly( ns, 'incrmaxabs', incrmaxabs );

/**
* @name incrmcovariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mcovariance}
*/
import incrmcovariance from './../../incr/mcovariance';
setReadOnly( ns, 'incrmcovariance', incrmcovariance );

/**
* @name incrmcv
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mcv}
*/
import incrmcv from './../../incr/mcv';
setReadOnly( ns, 'incrmcv', incrmcv );

/**
* @name incrmda
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mda}
*/
import incrmda from './../../incr/mda';
setReadOnly( ns, 'incrmda', incrmda );

/**
* @name incrme
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/me}
*/
import incrme from './../../incr/me';
setReadOnly( ns, 'incrme', incrme );

/**
* @name incrmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mean}
*/
import incrmean from './../../incr/mean';
setReadOnly( ns, 'incrmean', incrmean );

/**
* @name incrmeanabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/meanabs}
*/
import incrmeanabs from './../../incr/meanabs';
setReadOnly( ns, 'incrmeanabs', incrmeanabs );

/**
* @name incrmeanabs2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/meanabs2}
*/
import incrmeanabs2 from './../../incr/meanabs2';
setReadOnly( ns, 'incrmeanabs2', incrmeanabs2 );

/**
* @name incrmeanstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/meanstdev}
*/
import incrmeanstdev from './../../incr/meanstdev';
setReadOnly( ns, 'incrmeanstdev', incrmeanstdev );

/**
* @name incrmeanvar
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/meanvar}
*/
import incrmeanvar from './../../incr/meanvar';
setReadOnly( ns, 'incrmeanvar', incrmeanvar );

/**
* @name incrmgmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mgmean}
*/
import incrmgmean from './../../incr/mgmean';
setReadOnly( ns, 'incrmgmean', incrmgmean );

/**
* @name incrmgrubbs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mgrubbs}
*/
import incrmgrubbs from './../../incr/mgrubbs';
setReadOnly( ns, 'incrmgrubbs', incrmgrubbs );

/**
* @name incrmhmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mhmean}
*/
import incrmhmean from './../../incr/mhmean';
setReadOnly( ns, 'incrmhmean', incrmhmean );

/**
* @name incrmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/midrange}
*/
import incrmidrange from './../../incr/midrange';
setReadOnly( ns, 'incrmidrange', incrmidrange );

/**
* @name incrmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/min}
*/
import incrmin from './../../incr/min';
setReadOnly( ns, 'incrmin', incrmin );

/**
* @name incrminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/minabs}
*/
import incrminabs from './../../incr/minabs';
setReadOnly( ns, 'incrminabs', incrminabs );

/**
* @name incrminmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/minmax}
*/
import incrminmax from './../../incr/minmax';
setReadOnly( ns, 'incrminmax', incrminmax );

/**
* @name incrminmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/minmaxabs}
*/
import incrminmaxabs from './../../incr/minmaxabs';
setReadOnly( ns, 'incrminmaxabs', incrminmaxabs );

/**
* @name incrmmaape
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmaape}
*/
import incrmmaape from './../../incr/mmaape';
setReadOnly( ns, 'incrmmaape', incrmmaape );

/**
* @name incrmmae
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmae}
*/
import incrmmae from './../../incr/mmae';
setReadOnly( ns, 'incrmmae', incrmmae );

/**
* @name incrmmape
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmape}
*/
import incrmmape from './../../incr/mmape';
setReadOnly( ns, 'incrmmape', incrmmape );

/**
* @name incrmmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmax}
*/
import incrmmax from './../../incr/mmax';
setReadOnly( ns, 'incrmmax', incrmmax );

/**
* @name incrmmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmaxabs}
*/
import incrmmaxabs from './../../incr/mmaxabs';
setReadOnly( ns, 'incrmmaxabs', incrmmaxabs );

/**
* @name incrmmda
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmda}
*/
import incrmmda from './../../incr/mmda';
setReadOnly( ns, 'incrmmda', incrmmda );

/**
* @name incrmme
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mme}
*/
import incrmme from './../../incr/mme';
setReadOnly( ns, 'incrmme', incrmme );

/**
* @name incrmmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmean}
*/
import incrmmean from './../../incr/mmean';
setReadOnly( ns, 'incrmmean', incrmmean );

/**
* @name incrmmeanabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmeanabs}
*/
import incrmmeanabs from './../../incr/mmeanabs';
setReadOnly( ns, 'incrmmeanabs', incrmmeanabs );

/**
* @name incrmmeanabs2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmeanabs2}
*/
import incrmmeanabs2 from './../../incr/mmeanabs2';
setReadOnly( ns, 'incrmmeanabs2', incrmmeanabs2 );

/**
* @name incrmmeanstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmeanstdev}
*/
import incrmmeanstdev from './../../incr/mmeanstdev';
setReadOnly( ns, 'incrmmeanstdev', incrmmeanstdev );

/**
* @name incrmmeanvar
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmeanvar}
*/
import incrmmeanvar from './../../incr/mmeanvar';
setReadOnly( ns, 'incrmmeanvar', incrmmeanvar );

/**
* @name incrmmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmidrange}
*/
import incrmmidrange from './../../incr/mmidrange';
setReadOnly( ns, 'incrmmidrange', incrmmidrange );

/**
* @name incrmmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmin}
*/
import incrmmin from './../../incr/mmin';
setReadOnly( ns, 'incrmmin', incrmmin );

/**
* @name incrmminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mminabs}
*/
import incrmminabs from './../../incr/mminabs';
setReadOnly( ns, 'incrmminabs', incrmminabs );

/**
* @name incrmminmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mminmax}
*/
import incrmminmax from './../../incr/mminmax';
setReadOnly( ns, 'incrmminmax', incrmminmax );

/**
* @name incrmminmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mminmaxabs}
*/
import incrmminmaxabs from './../../incr/mminmaxabs';
setReadOnly( ns, 'incrmminmaxabs', incrmminmaxabs );

/**
* @name incrmmpe
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmpe}
*/
import incrmmpe from './../../incr/mmpe';
setReadOnly( ns, 'incrmmpe', incrmmpe );

/**
* @name incrmmse
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mmse}
*/
import incrmmse from './../../incr/mmse';
setReadOnly( ns, 'incrmmse', incrmmse );

/**
* @name incrmpcorr
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mpcorr}
*/
import incrmpcorr from './../../incr/mpcorr';
setReadOnly( ns, 'incrmpcorr', incrmpcorr );

/**
* @name incrmpcorr2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mpcorr2}
*/
import incrmpcorr2 from './../../incr/mpcorr2';
setReadOnly( ns, 'incrmpcorr2', incrmpcorr2 );

/**
* @name incrmpcorrdist
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mpcorrdist}
*/
import incrmpcorrdist from './../../incr/mpcorrdist';
setReadOnly( ns, 'incrmpcorrdist', incrmpcorrdist );

/**
* @name incrmpe
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mpe}
*/
import incrmpe from './../../incr/mpe';
setReadOnly( ns, 'incrmpe', incrmpe );

/**
* @name incrmprod
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mprod}
*/
import incrmprod from './../../incr/mprod';
setReadOnly( ns, 'incrmprod', incrmprod );

/**
* @name incrmrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mrange}
*/
import incrmrange from './../../incr/mrange';
setReadOnly( ns, 'incrmrange', incrmrange );

/**
* @name incrmrmse
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mrmse}
*/
import incrmrmse from './../../incr/mrmse';
setReadOnly( ns, 'incrmrmse', incrmrmse );

/**
* @name incrmrss
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mrss}
*/
import incrmrss from './../../incr/mrss';
setReadOnly( ns, 'incrmrss', incrmrss );

/**
* @name incrmse
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mse}
*/
import incrmse from './../../incr/mse';
setReadOnly( ns, 'incrmse', incrmse );

/**
* @name incrmstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mstdev}
*/
import incrmstdev from './../../incr/mstdev';
setReadOnly( ns, 'incrmstdev', incrmstdev );

/**
* @name incrmsum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/msum}
*/
import incrmsum from './../../incr/msum';
setReadOnly( ns, 'incrmsum', incrmsum );

/**
* @name incrmsumabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/msumabs}
*/
import incrmsumabs from './../../incr/msumabs';
setReadOnly( ns, 'incrmsumabs', incrmsumabs );

/**
* @name incrmsumabs2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/msumabs2}
*/
import incrmsumabs2 from './../../incr/msumabs2';
setReadOnly( ns, 'incrmsumabs2', incrmsumabs2 );

/**
* @name incrmsummary
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/msummary}
*/
import incrmsummary from './../../incr/msummary';
setReadOnly( ns, 'incrmsummary', incrmsummary );

/**
* @name incrmsumprod
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/msumprod}
*/
import incrmsumprod from './../../incr/msumprod';
setReadOnly( ns, 'incrmsumprod', incrmsumprod );

/**
* @name incrmvariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mvariance}
*/
import incrmvariance from './../../incr/mvariance';
setReadOnly( ns, 'incrmvariance', incrmvariance );

/**
* @name incrmvmr
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/mvmr}
*/
import incrmvmr from './../../incr/mvmr';
setReadOnly( ns, 'incrmvmr', incrmvmr );

/**
* @name incrnancount
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/nancount}
*/
import incrnancount from './../../incr/nancount';
setReadOnly( ns, 'incrnancount', incrnancount );

/**
* @name incrnanmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/nanmaxabs}
*/
import incrnanmaxabs from './../../incr/nanmaxabs';
setReadOnly( ns, 'incrnanmaxabs', incrnanmaxabs );

/**
* @name incrnanmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/nanmean}
*/
import incrnanmean from './../../incr/nanmean';
setReadOnly( ns, 'incrnanmean', incrnanmean );

/**
* @name incrnanmeanabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/nanmeanabs}
*/
import incrnanmeanabs from './../../incr/nanmeanabs';
setReadOnly( ns, 'incrnanmeanabs', incrnanmeanabs );

/**
* @name incrnanmstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/nanmstdev}
*/
import incrnanmstdev from './../../incr/nanmstdev';
setReadOnly( ns, 'incrnanmstdev', incrnanmstdev );

/**
* @name incrnanmsum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/nanmsum}
*/
import incrnanmsum from './../../incr/nanmsum';
setReadOnly( ns, 'incrnanmsum', incrnanmsum );

/**
* @name incrnanskewness
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/nanskewness}
*/
import incrnanskewness from './../../incr/nanskewness';
setReadOnly( ns, 'incrnanskewness', incrnanskewness );

/**
* @name incrnanstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/nanstdev}
*/
import incrnanstdev from './../../incr/nanstdev';
setReadOnly( ns, 'incrnanstdev', incrnanstdev );

/**
* @name incrnansum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/nansum}
*/
import incrnansum from './../../incr/nansum';
setReadOnly( ns, 'incrnansum', incrnansum );

/**
* @name incrnansumabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/nansumabs}
*/
import incrnansumabs from './../../incr/nansumabs';
setReadOnly( ns, 'incrnansumabs', incrnansumabs );

/**
* @name incrnansumabs2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/nansumabs2}
*/
import incrnansumabs2 from './../../incr/nansumabs2';
setReadOnly( ns, 'incrnansumabs2', incrnansumabs2 );

/**
* @name incrpcorr
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/pcorr}
*/
import incrpcorr from './../../incr/pcorr';
setReadOnly( ns, 'incrpcorr', incrpcorr );

/**
* @name incrpcorr2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/pcorr2}
*/
import incrpcorr2 from './../../incr/pcorr2';
setReadOnly( ns, 'incrpcorr2', incrpcorr2 );

/**
* @name incrpcorrdist
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/pcorrdist}
*/
import incrpcorrdist from './../../incr/pcorrdist';
setReadOnly( ns, 'incrpcorrdist', incrpcorrdist );

/**
* @name incrpcorrdistmat
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/pcorrdistmat}
*/
import incrpcorrdistmat from './../../incr/pcorrdistmat';
setReadOnly( ns, 'incrpcorrdistmat', incrpcorrdistmat );

/**
* @name incrpcorrmat
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/pcorrmat}
*/
import incrpcorrmat from './../../incr/pcorrmat';
setReadOnly( ns, 'incrpcorrmat', incrpcorrmat );

/**
* @name incrprod
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/prod}
*/
import incrprod from './../../incr/prod';
setReadOnly( ns, 'incrprod', incrprod );

/**
* @name incrrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/range}
*/
import incrrange from './../../incr/range';
setReadOnly( ns, 'incrrange', incrrange );

/**
* @name incrrmse
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/rmse}
*/
import incrrmse from './../../incr/rmse';
setReadOnly( ns, 'incrrmse', incrrmse );

/**
* @name incrrss
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/rss}
*/
import incrrss from './../../incr/rss';
setReadOnly( ns, 'incrrss', incrrss );

/**
* @name incrskewness
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/skewness}
*/
import incrskewness from './../../incr/skewness';
setReadOnly( ns, 'incrskewness', incrskewness );

/**
* @name incrstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/stdev}
*/
import incrstdev from './../../incr/stdev';
setReadOnly( ns, 'incrstdev', incrstdev );

/**
* @name incrsum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/sum}
*/
import incrsum from './../../incr/sum';
setReadOnly( ns, 'incrsum', incrsum );

/**
* @name incrsumabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/sumabs}
*/
import incrsumabs from './../../incr/sumabs';
setReadOnly( ns, 'incrsumabs', incrsumabs );

/**
* @name incrsumabs2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/sumabs2}
*/
import incrsumabs2 from './../../incr/sumabs2';
setReadOnly( ns, 'incrsumabs2', incrsumabs2 );

/**
* @name incrsummary
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/summary}
*/
import incrsummary from './../../incr/summary';
setReadOnly( ns, 'incrsummary', incrsummary );

/**
* @name incrsumprod
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/sumprod}
*/
import incrsumprod from './../../incr/sumprod';
setReadOnly( ns, 'incrsumprod', incrsumprod );

/**
* @name incrvariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/variance}
*/
import incrvariance from './../../incr/variance';
setReadOnly( ns, 'incrvariance', incrvariance );

/**
* @name incrvmr
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/vmr}
*/
import incrvmr from './../../incr/vmr';
setReadOnly( ns, 'incrvmr', incrvmr );

/**
* @name incrwmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/incr/wmean}
*/
import incrwmean from './../../incr/wmean';
setReadOnly( ns, 'incrwmean', incrwmean );


// EXPORTS //

export default ns;
